import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ChevronLeft, ChevronRight, Download, FileDown } from 'lucide-react'
import { TRPCClientError } from '@trpc/client'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { trpc } from '@/trpc/client'
import { downloadPdf } from '@/utils/download-pdf'
import { cn } from '@/utils/misc'

type Calendar = 'zub' | 'dh'
type Template = 'classic' | 'modern'

const CALENDAR_OPTIONS: { value: Calendar; label: string }[] = [
	{ value: 'zub', label: 'ZUB' },
	{ value: 'dh', label: 'DH' },
]

const TEMPLATE_OPTIONS: { value: Template; label: string }[] = [
	{ value: 'classic', label: 'Klasický' },
	{ value: 'modern', label: 'Moderní' },
]

/** Monday 00:00 (local) of the week `weeksAgo` weeks before the current one */
function mondayOf(weeksAgo: number): Date {
	const now = new Date()
	const daysSinceMonday = (now.getDay() + 6) % 7
	return new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday - 7 * weeksAgo)
}

function toIsoDate(date: Date): string {
	const month = String(date.getMonth() + 1).padStart(2, '0')
	const day = String(date.getDate()).padStart(2, '0')
	return `${date.getFullYear()}-${month}-${day}`
}

function formatWeekRange(monday: Date): string {
	const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6)
	return `${monday.getDate()}. ${monday.getMonth() + 1}. – ${sunday.getDate()}. ${sunday.getMonth() + 1}. ${sunday.getFullYear()}`
}

function weekLabel(weeksAgo: number): string {
	if (weeksAgo === 0) return 'Tento týden'
	if (weeksAgo === 1) return 'Minulý týden'
	return `Před ${weeksAgo} týdny`
}

function WeekPicker({ weeksAgo, onChange }: { weeksAgo: number; onChange: (weeksAgo: number) => void }) {
	const arrowClass =
		'rounded-xl border border-[#e8e4df] bg-white p-2 text-[#6b5c4c] transition-colors hover:border-[#d7ad6c] disabled:opacity-40 disabled:hover:border-[#e8e4df]'
	return (
		<div>
			<div className="mb-2 text-sm font-medium text-[#5c5142]">Týden</div>
			<div className="flex items-center gap-2">
				<button
					type="button"
					aria-label="Předchozí týden"
					onClick={() => onChange(weeksAgo + 1)}
					className={arrowClass}
				>
					<ChevronLeft className="h-4 w-4" />
				</button>
				<div className="min-w-56 rounded-xl border border-[#d7ad6c] bg-[#f5ebe0] px-4 py-2 text-center text-[#3d3428]">
					<div className="font-medium">{weekLabel(weeksAgo)}</div>
					<div className="text-xs text-[#6b5c4c]">{formatWeekRange(mondayOf(weeksAgo))}</div>
				</div>
				<button
					type="button"
					aria-label="Další týden"
					onClick={() => onChange(weeksAgo - 1)}
					disabled={weeksAgo === 0}
					className={arrowClass}
				>
					<ChevronRight className="h-4 w-4" />
				</button>
			</div>
		</div>
	)
}

// Server messages for these codes are written for staff; anything else
// (proxy timeout page, network drop) is not, and the server may still have
// finished saving the report.
const STAFF_FACING_CODES = new Set(['PRECONDITION_FAILED', 'INTERNAL_SERVER_ERROR', 'UNAUTHORIZED', 'NOT_FOUND'])

function describeError(err: unknown, fallback: string): string {
	if (err instanceof TRPCClientError && STAFF_FACING_CODES.has(err.data?.code)) {
		return err.message
	}
	return fallback
}

const GENERATE_FAILED =
	'Spojení se serverem selhalo. Report se možná přesto uložil – zkontrolujte historii níže, než to zkusíte znovu.'

function OptionGroup<T extends string>({
	label,
	options,
	value,
	onChange,
}: {
	label: string
	options: { value: T; label: string }[]
	value: T
	onChange: (value: T) => void
}) {
	return (
		<div>
			<div className="mb-2 text-sm font-medium text-[#5c5142]">{label}</div>
			<div className="flex gap-2">
				{options.map((option) => (
					<button
						key={option.value}
						type="button"
						onClick={() => onChange(option.value)}
						className={cn(
							'rounded-xl border px-4 py-2 transition-colors',
							value === option.value
								? 'border-[#d7ad6c] bg-[#f5ebe0] text-[#3d3428] font-medium'
								: 'border-[#e8e4df] bg-white text-[#6b5c4c] hover:border-[#d7ad6c]',
						)}
					>
						{option.label}
					</button>
				))}
			</div>
		</div>
	)
}

type CalendarFilter = Calendar | 'all'

const CALENDAR_FILTER_OPTIONS: { value: CalendarFilter; label: string }[] = [
	{ value: 'all', label: 'Vše' },
	...CALENDAR_OPTIONS,
]

const HISTORY_PAGE_SIZE = 12

const outlineButton =
	'gap-2 border-[#e8e4df] bg-white text-[#5c5142] hover:border-[#d7ad6c] hover:bg-[#faf8f5] hover:text-[#3d3428]'

/** YYYY-MM-DD → local midnight */
function parseIsoDate(value: string): Date {
	const [year, month, day] = value.split('-').map(Number)
	return new Date(year, month - 1, day)
}

function formatDateTime(value: Date): string {
	return new Date(value).toLocaleString('cs-CZ', {
		day: 'numeric',
		month: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}

function HistoryCard() {
	const [calendar, setCalendar] = useState<CalendarFilter>('all')
	const [visible, setVisible] = useState(HISTORY_PAGE_SIZE)
	const history = trpc.weeklyReport.history.useQuery({
		calendar: calendar === 'all' ? undefined : calendar,
	})
	const download = trpc.weeklyReport.download.useMutation()
	const [downloading, setDownloading] = useState<string | null>(null)
	const entries = history.data ?? []

	const handleDownload = async (filename: string) => {
		setDownloading(filename)
		try {
			const result = await download.mutateAsync({ filename })
			downloadPdf(result.pdfBase64, result.filename)
		} catch (err) {
			toast.error(describeError(err, 'Report se nepodařilo stáhnout.'))
			// A vanished file means the list is stale
			history.refetch()
		} finally {
			setDownloading(null)
		}
	}

	return (
		<div className="space-y-5 rounded-xl border border-[#e8e4df] bg-white px-5 py-5">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<h2 className="font-semibold text-[#3d3428]">Historie reportů</h2>
				<OptionGroup
					label="Kalendář"
					options={CALENDAR_FILTER_OPTIONS}
					value={calendar}
					onChange={(value) => {
						setCalendar(value)
						setVisible(HISTORY_PAGE_SIZE)
					}}
				/>
			</div>
			{history.isLoading ? (
				<LoadingSpinner />
			) : history.isError ? (
				<p className="text-sm text-red-700">{describeError(history.error, 'Historii reportů se nepodařilo načíst.')}</p>
			) : entries.length === 0 ? (
				<p className="text-sm text-[#6b5c4c]">Zatím nebyl vytvořen žádný report.</p>
			) : (
				<div className="-mx-5 overflow-x-auto px-5">
					<table className="w-full min-w-[560px] text-left text-sm">
						<thead className="border-b border-[#f0ece6] text-xs uppercase tracking-wide text-[#a09689]">
							<tr>
								<th className="py-2 pr-3 font-medium">Týden</th>
								<th className="py-2 pr-3 font-medium">Kalendář</th>
								<th className="py-2 pr-3 font-medium">Vytvořeno</th>
								<th className="py-2 font-medium">Stáhnout</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-[#f0ece6] text-[#3d3428]">
							{entries.slice(0, visible).map((entry) => (
								<tr key={`${entry.weekStart}_${entry.calendar}`} className="align-middle">
									<td className="whitespace-nowrap py-2 pr-3">{formatWeekRange(parseIsoDate(entry.weekStart))}</td>
									<td className="py-2 pr-3">
										<Badge variant="outline" className="border-[#d7ad6c] bg-[#f5ebe0] font-medium text-[#3d3428]">
											{entry.calendar.toUpperCase()}
										</Badge>
									</td>
									<td className="whitespace-nowrap py-2 pr-3">{formatDateTime(entry.generatedAt)}</td>
									<td className="py-2">
										<div className="flex flex-wrap gap-2">
											{TEMPLATE_OPTIONS.map(({ value, label }) => {
												const file = entry.files[value]
												if (!file) return null
												return (
													<Button
														key={value}
														type="button"
														variant="outline"
														size="sm"
														onClick={() => handleDownload(file.filename)}
														disabled={downloading !== null}
														className={outlineButton}
													>
														<Download className="h-4 w-4" />
														{downloading === file.filename ? 'Stahuji…' : label}
													</Button>
												)
											})}
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			{entries.length > visible && (
				<Button
					type="button"
					variant="outline"
					onClick={() => setVisible((count) => count + HISTORY_PAGE_SIZE)}
					className={outlineButton}
				>
					Načíst další
				</Button>
			)}
		</div>
	)
}

export function WeeklyReportSettingsPage() {
	const navigate = useNavigate()
	const [calendar, setCalendar] = useState<Calendar>('zub')
	const [template, setTemplate] = useState<Template>('classic')
	const [weeksAgo, setWeeksAgo] = useState(0)
	const utils = trpc.useUtils()
	// Refresh even on failure: the server may still have saved the PDFs
	const generate = trpc.weeklyReport.generate.useMutation({
		onSettled: () => utils.weeklyReport.history.invalidate(),
	})

	const handleGenerate = async () => {
		let result: Awaited<ReturnType<typeof generate.mutateAsync>>
		try {
			result = await generate.mutateAsync({
				calendar,
				template,
				// Omitted for the current week so the server's clock decides it
				weekStart: weeksAgo === 0 ? undefined : toIsoDate(mondayOf(weeksAgo)),
			})
		} catch (err) {
			toast.error(describeError(err, GENERATE_FAILED))
			return
		}
		const { patients, bookedThisWeek, bookedEarlier, withoutNext } = result.summary
		toast.success(
			`Report uložen. Pacientů: ${patients} · objednáno v daném týdnu: ${bookedThisWeek} · dříve: ${bookedEarlier} · bez termínu: ${withoutNext}`,
		)
		try {
			downloadPdf(result.pdfBase64, result.filename)
		} catch {
			toast.error('Report je uložený na serveru, ale stažení se nepodařilo.')
		}
	}

	return (
		<div className="min-h-screen bg-[#faf8f5]">
			<div className="mx-auto max-w-3xl px-6 py-6">
				<div className="mb-6 flex items-center justify-between">
					<button
						type="button"
						onClick={() => navigate(-1)}
						className="inline-flex items-center gap-2 text-[#5c5142] hover:text-[#3d3428] transition-colors"
					>
						<ArrowLeft className="h-4 w-4" />
						<span>Zpět</span>
					</button>
					<h1 className="text-2xl font-semibold text-[#3d3428]">Týdenní report</h1>
					<div className="w-16" />
				</div>

				<div className="space-y-6 rounded-xl border border-[#e8e4df] bg-white px-5 py-5">
					<p className="text-sm text-[#6b5c4c]">
						Pacienti, kteří ve vybraném týdnu přišli – termín nebyl zrušený ani
						nedostavený a pacient byl zaškrtnutý v Denním zápisu – a jejich další
						termín. Tento týden se počítá od pondělí do teď, minulé týdny podle
						stavu na konci týdne (neděle). Uloží se obě varianty PDF, vybraná se
						stáhne.
					</p>

					<WeekPicker weeksAgo={weeksAgo} onChange={setWeeksAgo} />
					<OptionGroup label="Kalendář" options={CALENDAR_OPTIONS} value={calendar} onChange={setCalendar} />
					<OptionGroup label="Vzhled PDF" options={TEMPLATE_OPTIONS} value={template} onChange={setTemplate} />

					<button
						type="button"
						onClick={handleGenerate}
						disabled={generate.isPending}
						className="inline-flex items-center gap-2 rounded-xl bg-[#c49b5a] px-5 py-2.5 font-medium text-white hover:bg-[#b38a4a] disabled:opacity-60 disabled:cursor-wait transition-colors"
					>
						<FileDown className="h-4 w-4" />
						{generate.isPending ? 'Vytvářím…' : 'Vygenerovat a stáhnout'}
					</button>
				</div>

				<div className="mt-6">
					<HistoryCard />
				</div>
			</div>
		</div>
	)
}

export default WeeklyReportSettingsPage
